import React from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import SEO from "../components/SEO";
import { getFAQSchema, getBreadcrumbSchema } from "../utils/schema";
import "./FAQ.css";

export const FAQ = () => {
  const faqs = [
    {
      category: "General Questions",
      questions: [
        {
          q: "What qualifications does your accountant hold?",
          a: "Our practice is led by a fully qualified FCCA (Fellow Chartered Certified Accountant) and MAAT (Member of the Association of Accounting Technicians) accountant with over 15 years of UK practice experience. These qualifications ensure the highest standards of technical competence and professional ethics.",
        },
        {
          q: "Which areas do you serve?",
          a: "We serve businesses and individuals across Wolverhampton, Dudley, Walsall, Bilston, Willenhall, West Bromwich, Sedgley, Wednesfield, and throughout the West Midlands. Our services are available to clients across the UK through our cloud accounting solutions.",
        },
        {
          q: "How do I get in touch?",
          a: "Simply email us at info@brightsonaccounting.co.uk with your enquiry. All emails are handled directly by our FCCA-qualified accountant, and you'll receive a response within 24 hours.",
        },
      ],
    },
    {
      category: "Tax Returns & Deadlines",
      questions: [
        {
          q: "When is the Self Assessment tax return deadline?",
          a: "For online Self Assessment tax returns, the deadline is 31st January following the end of the tax year (which ends on 5th April). For example, the 2024/25 tax year ends on 5th April 2025, and the online filing deadline is 31st January 2026. Paper returns must be filed by 31st October.",
        },
        {
          q: "What happens if I miss the tax return deadline?",
          a: "HMRC imposes automatic penalties for late filing. An immediate £100 penalty applies even if you have no tax to pay. Additional penalties accrue if the return is more than 3 months late. We recommend filing early to avoid these penalties and can assist with penalty appeals if circumstances warrant.",
        },
        {
          q: "What records does HMRC require me to keep?",
          a: "HMRC requires you to keep records of all income, expenses, VAT transactions (if registered), and supporting documents (invoices, receipts, bank statements) for at least 5 years after the 31st January submission deadline. For companies, records must be kept for 6 years from the end of the financial year.",
        },
      ],
    },
    {
      category: "Business Structure & Start-Ups",
      questions: [
        {
          q: "Should I operate as a sole trader or limited company?",
          a: "The choice depends on your circumstances. Sole trader status offers simplicity and lower administrative costs, while limited companies provide tax efficiency, limited liability protection, and a more professional image. We assess your specific situation - including income level, growth plans, and risk factors - to recommend the optimal structure.",
        },
        {
          q: "What's involved in company formation?",
          a: "Company formation involves registering with Companies House, creating articles of association, appointing directors and shareholders, and obtaining a company registration number. You'll also need to register for Corporation Tax with HMRC within 3 months. Our start-up support service handles all these registrations and setup requirements.",
        },
        {
          q: "Do I need to register for VAT?",
          a: "VAT registration becomes mandatory when your taxable turnover exceeds the current threshold (£90,000 for 2024/25). You can also register voluntarily if your turnover is below this threshold, which may be beneficial if you incur significant VAT on expenses. We can advise on whether voluntary registration makes sense for your business.",
        },
      ],
    },
    {
      category: "Allowable Expenses",
      questions: [
        {
          q: "What expenses can I claim as a sole trader?",
          a: "You can claim expenses that are 'wholly and exclusively' for business purposes, including: office costs, travel and vehicle expenses (business mileage or actual costs), professional fees, marketing, equipment, insurance, and premises costs. Home working expenses can also be claimed. We help maximize your allowable deductions while ensuring full HMRC compliance.",
        },
        {
          q: "Can I claim expenses for working from home?",
          a: "Yes. You can claim a proportion of your home costs (mortgage interest/rent, utilities, council tax, internet, insurance) based on business use. HMRC also offers simplified flat-rate deductions based on hours worked. For limited companies, directors can claim a monthly allowance for home office use. We calculate the most tax-efficient method for your circumstances.",
        },
        {
          q: "What vehicle expenses can I claim?",
          a: "You can either claim actual vehicle costs (fuel, insurance, repairs, depreciation) apportioned for business use, or use HMRC's simplified mileage rates (45p per mile for the first 10,000 miles, then 25p per mile). The best method depends on your mileage and vehicle costs. We analyze both to maximize your deduction.",
        },
      ],
    },
    {
      category: "VAT & Payroll",
      questions: [
        {
          q: "How often do I need to submit VAT returns?",
          a: "Most businesses submit VAT returns quarterly, covering 3-month periods. Some smaller businesses may be eligible for the Annual Accounting Scheme. Returns must be filed and payment made within one month and 7 days of the period end. Making Tax Digital (MTD) requirements mean you must use compatible software for VAT submissions.",
        },
        {
          q: "What are the VAT flat rate schemes?",
          a: "The Flat Rate Scheme simplifies VAT by allowing you to pay a fixed percentage of your turnover, rather than calculating VAT on every transaction. Available to businesses with turnover under £150,000, it can reduce admin and potentially save money depending on your expense profile. We assess whether this scheme benefits your business.",
        },
        {
          q: "What are my payroll obligations as an employer?",
          a: "Employers must operate PAYE (Pay As You Earn), deduct income tax and National Insurance from employee wages, make employer NI contributions, submit Real Time Information (RTI) reports to HMRC with each payroll run, and comply with auto-enrolment pension requirements. Our payroll service handles all these obligations, ensuring full compliance.",
        },
      ],
    },
    {
      category: "Pricing & Services",
      questions: [
        {
          q: "How do you charge for your services?",
          a: "We provide transparent fixed-fee quotes based on your specific requirements, business size, and complexity. Once agreed, the fee is fixed - no hourly rates or hidden charges. Monthly payment plans are available for ongoing services. Contact us for a personalized quote tailored to your needs.",
        },
        {
          q: "What's included in your fees?",
          a: "Our fees include all professional work, HMRC submissions, ongoing support, and unlimited email queries. Any additional services beyond the agreed scope are discussed and approved upfront. We believe in complete transparency with no unexpected charges.",
        },
      ],
    },
  ];

  // Flatten all FAQs for schema
  const allFAQs = faqs.flatMap(category => 
    category.questions.map(q => ({ question: q.q, answer: q.a }))
  );

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'FAQ', url: '/faq' }
  ]);

  const faqSchema = getFAQSchema(allFAQs);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [breadcrumbSchema, faqSchema]
  };

  return (
    <div className="faq-page">
      <SEO
        title="FAQ | Common Accounting Questions Answered | Wolverhampton Accountant"
        description="Answers to common accounting, tax, and business questions. Expert advice from FCCA-qualified accountant on tax returns, VAT, payroll, expenses, and HMRC compliance."
        canonical="/faq"
        keywords="accountant FAQ Wolverhampton, tax questions, accounting help, self assessment help, VAT questions"
        schema={combinedSchema}
      />
      {/* Hero Section */}
      <section className="faq-hero">
        <div className="container">
          <div className="faq-hero-content">
            <h1>Frequently Asked Questions</h1>
            <p>
              Expert answers to common accounting, tax, and business questions
              from our FCCA-qualified accountant
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="section faq-content">
        <div className="container">
          <div className="faq-container">
            {faqs.map((category, catIndex) => (
              <div key={catIndex} className="faq-category">
                <h2 className="category-title">{category.category}</h2>
                <Accordion type="single" collapsible className="faq-accordion">
                  {category.questions.map((faq, qIndex) => (
                    <AccordionItem
                      key={qIndex}
                      value={`${catIndex}-${qIndex}`}
                      className="faq-item"
                    >
                      <AccordionTrigger className="faq-question">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="faq-answer">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="section section-alt questions-cta">
        <div className="container">
          <div className="cta-box">
            <h2>Still Have Questions?</h2>
            <p>
              Can't find the answer you're looking for? Email our
              FCCA-qualified accountant directly for personalized advice on your
              specific situation.
            </p>
            <Link
              to="/contact"
              className="btn-primary btn-large"
              data-testid="faq-cta-contact"
            >
              <Mail size={22} />
              Ask Your Question
            </Link>
            <p className="cta-note">
              Response from a FCCA-qualified accountant within 24 hours
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
